import type { PersonOverlayAttributeKey } from "src/overlayAttributes/personOverlayAttributes";

/** Catalog / persist key. Construct with `overlayAttributeKey` at the wire seam. */
export type OverlayAttributeKey = string & {
  readonly __brand: "OverlayAttributeKey";
};

export const overlayAttributeKey = (
  value: string
): OverlayAttributeKey | null => {
  if (!value || value === "byKey") {
    return null;
  }
  return value as OverlayAttributeKey;
};

/** Workspace JSON and settings toggles. Hidden wins when more than one flag is set. */
export type AttributePolicyFlags = {
  hiddenFromPersonPanel: boolean;
  readOnlyInPersonPanel: boolean;
  defaultOnCards: boolean;
  allowAddingNew: boolean;
};

export type PolicyToggleField = keyof AttributePolicyFlags;

/**
 * Constructed policy. Hidden / read-only / editable cannot overlap.
 */
export type AttributeDisplayPolicy =
  | { kind: "hidden" }
  | { kind: "readOnly"; defaultOnCards: boolean }
  | { kind: "editable"; defaultOnCards: boolean; allowAddingNew: boolean };

export const policyFromFlags = (
  flags: Partial<AttributePolicyFlags>
): AttributeDisplayPolicy => {
  if (flags.hiddenFromPersonPanel) {
    return { kind: "hidden" };
  }
  if (flags.readOnlyInPersonPanel) {
    return { kind: "readOnly", defaultOnCards: flags.defaultOnCards ?? false };
  }
  return {
    kind: "editable",
    defaultOnCards: flags.defaultOnCards ?? false,
    allowAddingNew: flags.allowAddingNew ?? false,
  };
};

export const policyFlags = (
  policy: AttributeDisplayPolicy
): AttributePolicyFlags => {
  switch (policy.kind) {
    case "hidden":
      return {
        hiddenFromPersonPanel: true,
        readOnlyInPersonPanel: false,
        defaultOnCards: false,
        allowAddingNew: false,
      };
    case "readOnly":
      return {
        hiddenFromPersonPanel: false,
        readOnlyInPersonPanel: true,
        defaultOnCards: policy.defaultOnCards,
        allowAddingNew: false,
      };
    case "editable":
      return {
        hiddenFromPersonPanel: false,
        readOnlyInPersonPanel: false,
        defaultOnCards: policy.defaultOnCards,
        allowAddingNew: policy.allowAddingNew,
      };
    default: {
      const _exhaustive: never = policy;
      return _exhaustive;
    }
  }
};

export const DEFAULT_ATTRIBUTE_POLICY: AttributeDisplayPolicy = {
  kind: "editable",
  defaultOnCards: false,
  allowAddingNew: false,
};

export type AttributeCatalogEntry = PersonOverlayAttributeKey & {
  entityTypes: readonly AttributeEntityType[];
  systemReadOnly?: boolean;
  pickerExcluded?: boolean;
  settingsExcluded?: boolean;
  internal?: boolean;
  help?: string;
};

export const SYSTEM_READ_ONLY_FIELD_TOOLTIP = "System field — always read-only";

export type AttributeEntityType = "person" | "position" | "team";

export const ATTRIBUTE_ENTITY_TYPE_ORDER: readonly AttributeEntityType[] = [
  "person",
  "position",
  "team",
] as const;

export const ATTRIBUTE_ENTITY_TYPE_LABELS: Record<
  AttributeEntityType,
  { groupHeader: string; badge: string }
> = {
  person: { groupHeader: "People attributes", badge: "Person" },
  position: { groupHeader: "Position attributes", badge: "Position" },
  team: { groupHeader: "Team attributes", badge: "Team" },
};

export type AttributeTypeLabel = "Default" | "Custom" | "Internal";

export type AttributeVisibilityAudience = "All users" | "Power+" | "System";

export type AttributeShowHideLabel = "Show" | "Hide";

export type AttributeEditableLabel = "Editable" | "Read-only" | "N/A";

export type AttributeAddingLabel = "Allow new" | "Existing only" | "N/A";

export type AttributePolicyByKey = Partial<
  Record<string, AttributeDisplayPolicy>
>;

/** Test / story helper: wire-shaped bags to constructed policies. */
export const policiesFromFlagBags = (
  bags: Record<string, Partial<AttributePolicyFlags>>
): AttributePolicyByKey => {
  const policies: AttributePolicyByKey = {};
  for (const [key, flags] of Object.entries(bags)) {
    const branded = overlayAttributeKey(key);
    if (branded) {
      policies[branded] = policyFromFlags(flags);
    }
  }
  return policies;
};
